import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { FormState } from '../../SalaryCalculator/types';
import { MobiusTrackingContext } from '@/types/Mobius';
import getTrackingContext from '@/utils/getTrackingContext';
import type { RecommendedItem } from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/types';
import type {
  Degree as DegreeType,
  Subject as SubjectType,
  DropdownOptionItem,
} from '@/components/DesignSystem/Subsections/SalaryCalculator/types';
import { Occupation, Product } from '@/types/calculator';
import type {
  SALARY_CALCULATOR_CONFIG,
  DEGREE_LENGTH_CALCULATOR_CONFIG,
  CAREER_DETAILS_CONFIG,
} from '@/constants/calculatorConfigs';

// Comprehensive calculator state that includes both form state and calculator-specific values
interface CalculatorState {
  // DCS form state (degree, category, subject)
  formState: FormState;
  // Calculator-specific values (currentSalary, programCost, etc.)
  calculatorValues: Record<string, any>;
}

interface CalculatorContextValue {
  // Combined state
  state: CalculatorState;
  // Function to update DCS form fields
  handleFieldChange: (fieldName: string, value: string) => void;
  // Function to update calculator-specific values
  updateCalculatorValue: (key: string, value: any) => void;
  // Function to update multiple calculator values at once
  updateCalculatorValues: (values: Record<string, any>) => void;
  // Helper function to get a specific calculator value
  getCalculatorValue: (key: string) => any;
  // Helper function to get all calculator values
  calculatorValues: Record<string, any>;
  // Form completion check
  isFormComplete: () => boolean;
  // Program results from fetchPrograms
  results?: Product[];
  // Occupation data from fetchOccupations
  occupations?: Occupation[];
  // Loading states
  loadingPrograms?: boolean;
  loadingOccupations?: boolean;
  isCalculating?: boolean;
  setIsCalculating?: (isCalculating: boolean) => void;
  variant?: string;
  config?:
    | typeof SALARY_CALCULATOR_CONFIG
    | typeof DEGREE_LENGTH_CALCULATOR_CONFIG
    | typeof CAREER_DETAILS_CONFIG;
  // Manual trigger function for data fetching
  triggerDataFetch?: () => void;
  // Flag to indicate if initial fetch has been completed
  hasInitialFetch?: boolean;
  // Helper methods to return transformed data
  getSelectedDegree: () => DegreeType | null;
  getSelectedSubject: () => SubjectType | null;
  getRecommendedItems: () => RecommendedItem[];
  trackingContext?: MobiusTrackingContext;
  // Correlation ID for tracking related events
  correlationId?: string;
  setCorrelationId?: (correlationId: string) => void;
}

const CalculatorContext = createContext<CalculatorContextValue | undefined>(
  undefined,
);

interface CalculatorProviderProps {
  children: ReactNode;
  formState: FormState;
  handleFieldChange: (fieldName: string, value: string) => void;
  variant?: string;
  config?: any;
  initialCalculatorValues?: Record<string, any>;
  onCalculatorValueChange?: (key: string, value: any) => void;
  onCalculatorValuesChange?: (values: Record<string, any>) => void;
  results?: any[];
  occupations?: any[];
  loadingPrograms?: boolean;
  loadingOccupations?: boolean;
  isCalculating?: boolean;
  setIsCalculating?: (isCalculating: boolean) => void;
  triggerDataFetch?: () => void;
  hasInitialFetch?: boolean;
}

export const CalculatorProvider: React.FC<CalculatorProviderProps> = ({
  children,
  formState,
  handleFieldChange,
  variant,
  config,
  initialCalculatorValues = {},
  onCalculatorValueChange,
  onCalculatorValuesChange,
  results = [],
  occupations = [],
  loadingPrograms = false,
  loadingOccupations = false,
  isCalculating: externalIsCalculating = false,
  setIsCalculating: externalSetIsCalculating,
  triggerDataFetch,
  hasInitialFetch = false,
}) => {
  // State for calculator-specific values (currentSalary, programCost, etc.)
  // Include DCS selections in calculator values for centralized state management
  const [calculatorValues, setCalculatorValues] = useState<Record<string, any>>(
    () => ({
      // Include DCS form state in calculator values
      degree: formState?.current?.degree || '',
      category: formState?.current?.category || '',
      subject: formState?.current?.subject || '',
      // Include any additional initial calculator values
      ...initialCalculatorValues,
    }),
  );

  // Internal calculating state (fallback if not provided externally)
  const [internalIsCalculating, setInternalIsCalculating] =
    useState<boolean>(false);

  // Correlation ID state for tracking related events
  const [correlationId, setCorrelationId] = useState<string>('');

  const [trackingContext, setTrackingContext] = useState<MobiusTrackingContext>(
    {
      formatSubtype: config.tracking.cardformatSubtype || '',
      formatType: 'dexp-tool-results',
    },
  );

  // Use external calculating state if provided, otherwise use internal
  const isCalculating = externalIsCalculating;
  const setIsCalculating = externalSetIsCalculating || setInternalIsCalculating;

  // Keep calculator values in sync with form state changes
  useEffect(() => {
    setCalculatorValues((prev) => {
      const newValues: Record<string, any> = {
        ...prev,
        degree: formState?.current?.degree || '',
        category: formState?.current?.category || '',
        subject: formState?.current?.subject || '',
      };

      // Auto-populate degree-type when degree changes from form state
      const degreeValue = formState?.current?.degree;
      if (degreeValue && degreeValue !== prev.degree) {
        const validDegreeTypes = [
          'associates',
          'bachelors',
          'masters',
          'doctorate',
        ];

        if (validDegreeTypes.includes(degreeValue)) {
          newValues['degree-type'] = degreeValue;

          // Also auto-populate totalCreditsRequired based on the degree type
          const degreeCreditsMap: Record<string, number> = {
            associates: 60,
            bachelors: 120,
            masters: 36,
            doctorate: 60,
          };

          const requiredCredits = degreeCreditsMap[degreeValue];
          if (requiredCredits) {
            newValues.totalCreditsRequired = requiredCredits;
          }
        }
      }

      return newValues;
    });
  }, [
    formState?.current?.degree,
    formState?.current?.category,
    formState?.current?.subject,
  ]);

  useEffect(() => {
    const loadTrackingContext = async () => {
      try {
        const enhancedTrackingContext =
          await getTrackingContext(trackingContext);
        setTrackingContext(enhancedTrackingContext);
      } catch (error) {
        console.error('Error loading tracking context:', error);
      }
    };

    loadTrackingContext();
  }, []);

  // Function to update a single calculator value
  const updateCalculatorValue = useCallback(
    (key: string, value: any) => {
      const newValues = { ...calculatorValues, [key]: value };

      // Auto-populate totalCreditsRequired when degree-type changes
      // Degree types have predefined credit requirements:
      // Associates: 60 credits, Bachelors: 120 credits, Masters: 36 credits, Doctorate: 60 credits
      if (key === 'degree-type' && value) {
        const degreeCreditsMap: Record<string, number> = {
          associates: 60,
          bachelors: 120,
          masters: 36,
          doctorate: 60,
        };

        const requiredCredits = degreeCreditsMap[value];
        if (requiredCredits) {
          newValues.totalCreditsRequired = requiredCredits;
        }
      }

      setCalculatorValues(newValues);

      // If updating DCS values, also update form state to keep them in sync
      if (key === 'degree' || key === 'category' || key === 'subject') {
        handleFieldChange(key, value);
      }

      onCalculatorValueChange?.(key, value);

      // If we auto-populated totalCreditsRequired, notify about that change too
      if (
        key === 'degree-type' &&
        newValues.totalCreditsRequired !== calculatorValues.totalCreditsRequired
      ) {
        onCalculatorValueChange?.(
          'totalCreditsRequired',
          newValues.totalCreditsRequired,
        );
      }
    },
    [calculatorValues, onCalculatorValueChange, handleFieldChange],
  );

  // Function to update multiple calculator values
  const updateCalculatorValues = useCallback(
    (values: Record<string, any>) => {
      const newValues = { ...calculatorValues, ...values };

      // Auto-populate totalCreditsRequired when degree-type changes
      if (values['degree-type']) {
        const degreeCreditsMap: Record<string, number> = {
          associates: 60,
          bachelors: 120,
          masters: 36,
          doctorate: 60,
        };

        const requiredCredits = degreeCreditsMap[values['degree-type']];
        if (requiredCredits) {
          newValues.totalCreditsRequired = requiredCredits;
        }
      }

      setCalculatorValues(newValues);

      // If updating DCS values, also update form state to keep them in sync
      Object.entries(values).forEach(([key, value]) => {
        if (key === 'degree' || key === 'category' || key === 'subject') {
          handleFieldChange(key, value);
        }
      });

      onCalculatorValuesChange?.(values);

      // If we auto-populated totalCreditsRequired, notify about that change too
      if (
        values['degree-type'] &&
        newValues.totalCreditsRequired !== calculatorValues.totalCreditsRequired
      ) {
        const updatedValues = {
          ...values,
          totalCreditsRequired: newValues.totalCreditsRequired,
        };
        onCalculatorValuesChange?.(updatedValues);
      }
    },
    [calculatorValues, onCalculatorValuesChange, handleFieldChange],
  );

  // Helper function to get a specific calculator value
  const getCalculatorValue = useCallback(
    (key: string) => {
      return calculatorValues[key];
    },
    [calculatorValues],
  );

  // Helper methods to return transformed data
  const getSelectedDegree = useCallback((): DegreeType | null => {
    const currentDegreeSlug = getCalculatorValue('degree');
    const degreeOption = formState?.options?.degree?.find(
      (opt: DropdownOptionItem) => opt.slug === currentDegreeSlug,
    );
    return degreeOption
      ? {
          id: degreeOption.id,
          name: degreeOption.name,
          slug: degreeOption.slug,
        }
      : null;
  }, [formState?.options?.degree, getCalculatorValue]);

  const getSelectedSubject = useCallback((): SubjectType | null => {
    const currentSubjectSlug = getCalculatorValue('subject');
    const subjectOption = formState?.options?.subject?.find(
      (opt: DropdownOptionItem) => opt.slug === currentSubjectSlug,
    );
    return subjectOption
      ? {
          id: subjectOption.id,
          name: subjectOption.name,
          slug: subjectOption.slug,
        }
      : null;
  }, [formState?.options?.subject, getCalculatorValue]);

  const getRecommendedItems = useCallback((): RecommendedItem[] => {
    return (results || []).map((program) => ({
      id:
        typeof program.id === 'string'
          ? parseInt(program.id, 10) || 0
          : program.id || 0,
      subheader: program.schoolName || '',
      title: program.name || '',
      description: program.description || '',
      destinationUrl: program.destinationUrl || program.url || '',
      logo: program.schoolSlug
        ? `https://simple-storage-server.highereducation.com/${program.schoolSlug}.png`
        : undefined,
      schoolName: program.schoolName || '',
      name: program.name || '',
      listId: program.listId || '',
      capId: program.capId || '',
      isUserRequested: program.isUserRequested || false,
    }));
  }, [results]);

  // Form completion check
  const isFormComplete = useCallback(() => {
    if (!config?.fields) return false;

    return config.fields
      .filter((field: any) => field.required)
      .every((field: any) => {
        const value = calculatorValues[field.key];

        // Special handling for category fields with nested subjects
        if (field.key === 'category' && field.nestedSubjects) {
          // For nested subjects, we need both category and subject to be set
          const categoryValue = calculatorValues['category'];
          const subjectValue = calculatorValues['subject'];
          return categoryValue && subjectValue;
        }

        return value !== undefined && value !== null && value !== '';
      });
  }, [config?.fields, calculatorValues]);

  const value = useMemo(
    () => ({
      state: {
        formState,
        calculatorValues,
      },
      handleFieldChange,
      updateCalculatorValue,
      updateCalculatorValues,
      getCalculatorValue,
      calculatorValues,
      isFormComplete,
      results,
      occupations,
      loadingPrograms,
      loadingOccupations,
      isCalculating,
      setIsCalculating,
      variant,
      config,
      triggerDataFetch,
      hasInitialFetch,
      getSelectedDegree,
      getSelectedSubject,
      getRecommendedItems,
      trackingContext,
      correlationId,
      setCorrelationId,
    }),
    [
      formState,
      calculatorValues,
      handleFieldChange,
      updateCalculatorValue,
      updateCalculatorValues,
      getCalculatorValue,
      calculatorValues,
      isFormComplete,
      results,
      occupations,
      loadingPrograms,
      loadingOccupations,
      isCalculating,
      setIsCalculating,
      variant,
      config,
      triggerDataFetch,
      hasInitialFetch,
      getSelectedDegree,
      getSelectedSubject,
      getRecommendedItems,
      trackingContext,
      correlationId,
      setCorrelationId,
    ],
  );
  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  );
};

export const useCalculatorContext = () => {
  const context = useContext(CalculatorContext);
  if (context === undefined) {
    throw new Error(
      'useCalculatorContext must be used within a CalculatorProvider',
    );
  }
  return context;
};

export default CalculatorContext;
